import { useEffect, useRef, useState } from 'react';
import { translateApiError } from '../utils/translateApiError';
import type { FormEvent } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import toast from 'react-hot-toast';
import { Sparkles, Zap, Building2, Globe2, BarChart3, BrainCircuit, Rocket, Inbox, DoorOpen, Users2, ChevronLeft, ChevronRight, Link2, Bot, Send, TrendingUp, HeartHandshake, Shield, PlayCircle } from 'lucide-react';
import api from '../services/api';
import { trackLandingCTA, trackPricingClick, trackContactSubmit, trackNavClick, trackSignupClick } from '../services/analytics';
import SEO from '../components/SEO';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import './LandingPage.css';
import { useTranslation } from "react-i18next";

interface BlogPostPreview {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  tags: string[] | null;
  authorName: string;
  publishedAt: string;
}

/* ───── SVG Helpers ───── */
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const CheckCircle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function LandingPage() {
    const { t, i18n } = useTranslation();
  const [formSent, setFormSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnnual, setIsAnnual] = useState(true);
  const [blogPosts, setBlogPosts] = useState<BlogPostPreview[]>(() => {
    if (typeof window !== 'undefined') {
      const el = document.getElementById('initial-blog-posts');
      if (el && el.textContent) {
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error('Failed to parse initial blog posts', e);
        }
      }
    }
    return [];
  });
  const sliderRef = useRef<HTMLDivElement>(null);



  /* ── Intersection Observer for reveal animations ── */
  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); } }),
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* ── Auto-scroll logic for slider ── */
  useEffect(() => {
    const interval = setInterval(() => {
      if (sliderRef.current) {
        const slider = sliderRef.current;
        const scrollWidth = slider.scrollWidth;
        const clientWidth = slider.clientWidth;
        let newScrollLeft = slider.scrollLeft + clientWidth;
        if (newScrollLeft >= scrollWidth) {
          newScrollLeft = 0;
        }
        slider.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      }
    }, 7000); // Increased interval to 7 seconds for larger cards
    return () => clearInterval(interval);
  }, []);

  const scrollSlider = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const slider = sliderRef.current;
      const scrollAmount = slider.clientWidth * 0.8;
      slider.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  /* ── Fetch latest blog posts ── */
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
    fetch(`${baseUrl}/blog/posts?page=0&size=3&lang=${i18n.language}`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setBlogPosts(data.content || []))
      .catch(() => { });
  }, [i18n.language]);

  /* ── Smooth scroll helper (for in-page CTA links) ── */
  const scrollTo = (id: string) => {
    trackNavClick(id);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  /* ── Contact form ── */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      subject: formData.get('subject'),
      message: formData.get('message'),
    };

    try {
      await api.post('/contact', data);
      trackContactSubmit();
      setFormSent(true);
      form.reset();
      setTimeout(() => setFormSent(false), 3000);
    } catch (error: any) {
      console.error('Contact form error:', error);
      toast.error('Error: ' + (translateApiError(error, t, 'Unknown error')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={t('landing.aiPoweredGoogleFacebookReviewManagement')}
        description="Reviio crafts perfect, on-brand replies to your Google & Facebook reviews - automatically, in any language. Save hours, delight customers, and boost your reputation."
        schema={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Reviio",
          "operatingSystem": "Web",
          "applicationCategory": "BusinessApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "AI-powered Google & Facebook review management. Reply faster, extract actionable insights, generate review summaries, and track action items."
        }}
      />
      
      {/* ===== NAVBAR ===== */}
      <SiteHeader variant="transparent" />

      {/* ===== HERO ===== */}
      <main>
        <section className="lp-hero" id="hero">
          <div className="lp-container">
            <div className="lp-hero-badge">
              <span className="dot" />{' ' + t('common.aiPoweredReviewManagement')}</div>
            <h1>{' ' + t('landing.neverMissAReview')}<br />
              <span className="lp-gradient-text">{t('landing.replyInstantlyWithAi')}</span>
            </h1>
            <p className="lp-hero-description">{' ' + t('landing.reviioCraftsPerfectOnBrandReplies')}</p>
            <div className="lp-hero-actions">
              <Link to="/signup" className="lp-btn-primary lp-btn-primary-lg" id="hero-cta-signup" onClick={() => { trackLandingCTA('hero_start_free_trial'); trackSignupClick('hero'); }}>{t('landing.startFreeTrial')}</Link>
              <a href="#how-it-works" className="lp-btn-hero-secondary" onClick={(e) => { e.preventDefault(); scrollTo('how-it-works'); trackLandingCTA('hero_see_how_it_works'); }}>{' ' + t('landing.seeHowItWorks')}</a>
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className="lp-how-it-works" id="how-it-works">
          <div className="lp-container-wide">
            <div className="lp-section-label lp-reveal">{t('common.howItWorks')}</div>
            <h2 className="lp-section-title lp-reveal">{t('landing.fiveStepsToEffortlessReplies')}</h2>
            <p className="lp-section-subtitle lp-reveal">{t('landing.connectYourGoogleBusinessProfileAnd')}</p>
            <div className="lp-steps-grid">
              {[
                { num: '1', icon: <Link2 size={40} strokeWidth={1.5} />, title: t('landing.connectYourBusiness'), desc: t('landing.linkYourGoogleBusinessProfileIn') },
                { num: '2', icon: <Bot size={40} strokeWidth={1.5} />, title: t('landing.aiCraftsReplies'), desc: t('landing.forEveryNewReviewOurAi') },
                { num: '3', icon: <Send size={40} strokeWidth={1.5} />, title: t('landing.publishAutomatically'), desc: t('landing.chooseToAutoPublishInstantlyAfter') },
                { num: '4', icon: <TrendingUp size={40} strokeWidth={1.5} />, title: t('landing.gainAiInsights'), desc: t('landing.getActionableAiReportsDetailingWhat') },
                { num: '5', icon: <Shield size={40} strokeWidth={1.5} />, title: t('landing.negativeReviewGuard'), desc: t('landing.interceptNegativeFeedbackAndKeepIt') },
              ].map((s, i) => (
                <div key={s.num} className={`lp-step-card lp-reveal lp-reveal-delay-${i + 1}`}>
                  <div className="lp-step-number">{s.num}</div>
                  <div className="lp-step-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FEATURES SHOWCASE (NEW) ===== */}
        <section className="lp-features-showcase" id="features">
          <div className="lp-container-fluid">
            <div className="lp-section-label lp-reveal">{t('common.features')}</div>
            <h2 className="lp-section-title lp-reveal">{t('landing.everythingYouNeedToMasterYour')}</h2>
            <p className="lp-section-subtitle lp-reveal">{t('landing.powerfulToolsDesignedForRestaurantsShops')}</p>
            
            <div className="lp-slider-wrapper">
              <button className="lp-slider-arrow lp-slider-arrow-left lp-reveal" onClick={() => scrollSlider('left')} aria-label={t('landing.scrollLeft')}>
                <ChevronLeft size={32} />
              </button>
              
              <div className="lp-slider-container" ref={sliderRef}>
                <div className="lp-slider-track">
                {[
                  { slug: 'ai-reply-suggestions', icon: <Sparkles size={36} />, title: t('landing.aiReplySuggestions'), desc: t('landing.instantlyGenerate3UniqueContextAware') },
                  { slug: 'auto-reply-modes', icon: <Zap size={36} />, title: t('landing.autoReplyCustomRules'), desc: t('landing.putYourReviewManagementOnAutopilot') },
                  { slug: 'multi-location-management', icon: <Building2 size={36} />, title: t('landing.multiLocationManagement'), desc: t('landing.seamlesslyOverseeAllYourBranchesFrom') },
                  { slug: 'multi-language-support', icon: <Globe2 size={36} />, title: t('landing.multiLanguageSupport'), desc: t('landing.breakDownLanguageBarriersAutomaticallyDe') },
                  { slug: 'review-analytics', icon: <BarChart3 size={36} />, title: t('landing.reviewAnalyticsDashboard'), desc: t('landing.transformRawFeedbackIntoActionableIntell') },
                  { slug: 'review-insights', icon: <BrainCircuit size={36} />, title: t('common.reviewInsights'), desc: t('landing.diveDeeperIntoCustomerSentimentOur') },
                  { slug: 'review-campaigns', icon: <Rocket size={36} />, title: t('common.reviewCampaigns'), desc: t('landing.proactivelyBuildYourOnlineReputationAuto') },
                  { slug: 'feedback-inbox', icon: <Inbox size={36} />, title: t('common.feedbackInbox'), desc: t('landing.keepNegativeExperiencesOfflineProvideA') },
                  { slug: 'review-gate', icon: <DoorOpen size={36} />, title: t('landing.reviewGateFunnel'), desc: t('landing.intelligentlyRouteYourCustomersEncourage') },
                  { slug: 'team-management', icon: <Users2 size={36} />, title: t('landing.teamManagementRoles'), desc: t('landing.collaborateSecurelyAcrossYourOrganizatio') },
                ].map((f, i) => (
                  <Link to={`/features/${f.slug}`} key={f.slug} className={`lp-showcase-card lp-reveal lp-reveal-delay-${(i % 3) + 1}`}>
                    <div className="lp-showcase-icon">{f.icon}</div>
                    <div className="lp-showcase-content">
                      <h3>{f.title}</h3>
                      <p>{f.desc}</p>
                      <span className="lp-showcase-link">{t('landing.learnHow')}</span>
                    </div>
                  </Link>
                ))}
                </div>
              </div>

              <button className="lp-slider-arrow lp-slider-arrow-right lp-reveal" onClick={() => scrollSlider('right')} aria-label={t('landing.scrollRight')}>
                <ChevronRight size={32} />
              </button>
            </div>
          </div>
        </section>

        {/* ===== CTA BANNER ===== */}
        <section className="lp-cta-banner">
          <div className="lp-container">
            <h2 className="lp-reveal">{t('landing.readyToTransformYour')}<br />{t('landing.reviewManagement')}</h2>
            <p className="lp-reveal">{t('landing.joinHundredsOfBusinessesThatNever')}</p>
            <div className="lp-cta-banner-actions lp-reveal">
              <Link to="/signup" className="lp-btn-primary lp-btn-primary-lg" id="cta-signup">{t('common.claimYourFreeTrial')}</Link>
              <a href="#pricing" className="lp-btn-hero-secondary" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }}>{' ' + t('landing.viewPricing')}</a>
            </div>
          </div>
        </section>

        {/* ===== WHY SPEED MATTERS ===== */}
        <section className="lp-features lp-features-secondary">
          <div className="lp-container">
            <div className="lp-section-label lp-reveal">{t('landing.whySpeedMatters')}</div>
            <h2 className="lp-section-title lp-reveal">{t('landing.theImportanceOfQuickReplies')}</h2>
            <p className="lp-section-subtitle lp-reveal">{t('landing.respondingToReviewsInUnder24')}</p>
            <div className="lp-features-grid lp-features-grid-auto">
              <div className="lp-feature-card lp-reveal">
                <div className="lp-feature-icon"><TrendingUp size={28} color="#fff" /></div>
                <h3>{t('landing.boostsLocalSeo')}</h3>
                <p>{t('landing.googleRewardsBusinessesThatActivelyManag')}</p>
                <a href="https://support.google.com/business/answer/3474050" target="_blank" rel="noopener noreferrer" className="lp-feature-link">{t('landing.readGoogleSGuidelines')}</a>
              </div>
              <div className="lp-feature-card lp-reveal lp-reveal-delay-1">
                <div className="lp-feature-icon"><HeartHandshake size={28} color="#fff" /></div>
                <h3>{t('landing.buildsCustomerTrust')}</h3>
                <p>{t('landing.77OfConsumersSayThatSeeing')}</p>
                <a href="https://hbr.org/2018/02/study-replying-to-customer-reviews-results-in-better-ratings" target="_blank" rel="noopener noreferrer" className="lp-feature-link">{t('landing.readTheHarvardStudy')}</a>
              </div>
              <div className="lp-feature-card lp-reveal lp-reveal-delay-2">
                <div className="lp-feature-icon"><Bot size={28} color="#fff" /></div>
                <h3>{t('landing.askTheAi')}</h3>
                <p>{t('landing.donTJustTakeOurWord')}</p>
                <div className="lp-feature-actions">
                  <a href="https://chatgpt.com/?q=Why+is+it+crucial+for+local+businesses+to+respond+to+Google+Maps+reviews+within+24+hours%3F" target="_blank" rel="noopener noreferrer" className="lp-btn-primary lp-btn-primary-sm">{t('landing.askChatgpt')}</a>
                  <a href="https://claude.ai/new?q=Why+is+it+crucial+for+local+businesses+to+respond+to+Google+Maps+reviews+within+24+hours%3F" target="_blank" rel="noopener noreferrer" className="lp-btn-outline-secondary">{t('landing.askClaude')}</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== DEMO SHOWCASE ===== */}
        <section className="lp-demo-showcase">
          <div className="lp-container">
            <div className="lp-demo-content">
              <div className="lp-demo-text lp-reveal">
                <div className="lp-section-label">{t('landing.seeItInAction')}</div>
                <h3>{t('landing.aiThatUnderstands')}<br />{t('landing.yourBusiness')}</h3>
                <div className="lp-demo-list">
                  {[
                    t('landing.uploadMenusBrochuresAndImagesAi'),
                    t('landing.setPlayfulProfessionalOrSeriousTones'),
                    t('landing.fixedTemplatedRepliesFor5Star'),
                    t('landing.realTimeNotificationsOnMobileEmail'),
                  ].map((text) => (
                    <div key={text} className="lp-demo-list-item">
                      <div className="lp-demo-list-check"><CheckCircle /></div>
                      <p>{text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lp-demo-visual lp-reveal lp-reveal-delay-2">
                <div className="lp-demo-card">
                  <div className="lp-demo-review">
                    <div className="lp-demo-review-header">
                      <div className="lp-demo-avatar">M</div>
                      <div className="lp-demo-review-meta">
                        <h4>{t('landing.mariaG')}</h4>
                        <div className="stars">★★★★☆</div>
                      </div>
                    </div>
                    <p className="lp-demo-review-text">{t('landing.greatFoodButWeWaited45')}</p>
                  </div>
                  <div className="lp-demo-reply-label">{' ' + t('landing.aiGeneratedReply')}<span className="lp-ai-badge">{t('landing.suggestion1Of3')}</span>
                  </div>
                  <p className="lp-demo-reply-text">
                    {"\"" + t('landing.hiMariaThankYouForDining') + "\""}
                  </p>
                  
                  <div className="lp-demo-insights">
                    <div className="lp-demo-insights-header">
                      <BrainCircuit size={16} />{' ' + t('landing.aiInsightsReport')}</div>
                    <div className="lp-demo-insights-body">
                      <div className="lp-demo-insight positive">
                        <strong>{t('landing.loved')}</strong>{' ' + t('landing.foodQualityFriendlyStaff')}</div>
                      <div className="lp-demo-insight negative">
                        <strong>{t('landing.flagged')}</strong>{' ' + t('landing.45MinuteWaitTimeReservationSystem')}</div>
                    </div>
                  </div>

                  <div className="lp-demo-reply-actions">
                    <button className="lp-demo-btn lp-demo-btn-publish">{t('landing.publish')}</button>
                    <button className="lp-demo-btn lp-demo-btn-regen">{t('landing.regenerate')}</button>
                    <button className="lp-demo-btn lp-demo-btn-regen">{t('common.next2')}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WALKTHROUGHS ===== */}
        <section className="lp-features lp-features-light">
          <div className="lp-container">
            <div className="lp-section-label lp-reveal">{t('landing.interactiveDemos')}</div>
            <h2 className="lp-section-title lp-reveal">{t('landing.watchReviioInAction')}</h2>
            <p className="lp-section-subtitle lp-reveal">{t('landing.takeAGuidedTourOfThe')}</p>
            <div className="lp-features-grid">
              
              <Link to="/walkthroughs/app" className="lp-feature-card lp-feature-card-link lp-reveal">
                <div className="lp-feature-icon lp-feature-icon-primary"><PlayCircle size={28} color="#fff" /></div>
                <h3>{t('common.fullPlatformDemo')}</h3>
                <p>{t('landing.takeAComprehensiveTourOfThe')}</p>
                <div className="lp-feature-link lp-feature-link-spaced">{t('landing.watchDemo')}</div>
              </Link>
              
              <Link to="/walkthroughs/insights" className="lp-feature-card lp-feature-card-link lp-reveal lp-reveal-delay-1">
                <div className="lp-feature-icon lp-feature-icon-primary"><BrainCircuit size={28} color="#fff" /></div>
                <h3>{t('common.aiReviewInsights')}</h3>
                <p>{t('landing.watchHowOurAiAnalyzesThousands')}</p>
                <div className="lp-feature-link lp-feature-link-spaced">{t('landing.watchDemo')}</div>
              </Link>

              <Link to="/walkthroughs/settings" className="lp-feature-card lp-feature-card-link lp-reveal lp-reveal-delay-2">
                <div className="lp-feature-icon lp-feature-icon-primary"><Zap size={28} color="#fff" /></div>
                <h3>{t('landing.locationCustomization')}</h3>
                <p>{t('landing.explorePowerfulSettingsPerLocationAi')}</p>
                <div className="lp-feature-link lp-feature-link-spaced">{t('landing.watchDemo')}</div>
              </Link>

            </div>
          </div>
        </section>

        {/* ===== PRICING ===== */}
        <section className="lp-pricing" id="pricing">
          <div className="lp-container">
            <div className="lp-section-label lp-reveal">{t('common.pricing')}</div>
            <h2 className="lp-section-title lp-reveal">{t('landing.launchPromotion')}</h2>
            <p className="lp-section-subtitle lp-reveal">{' ' + t("landing.launchOfferEveryNewAccountGets")}
            </p>
            <div className="lp-pricing-toggle-container lp-reveal">
              <div 
                className={`lp-toggle-switch ${isAnnual ? 'active' : ''}`} 
                onClick={() => setIsAnnual(!isAnnual)}
              >
                <div className="lp-toggle-knob"></div>
              </div>
              <span 
                className="lp-pricing-toggle-label"
                onClick={() => setIsAnnual(!isAnnual)}
              >{' ' + t('common.billedAnnually')}<span className="lp-badge lp-pricing-badge">{t('common.save34')}</span>
              </span>
            </div>

            <div className="lp-pricing-grid">
              {/* Free */}
              <div className="lp-pricing-card lp-reveal lp-reveal-delay-1">
                <div className="lp-pricing-plan-name">{t('common.free')}</div>
                <div className="lp-pricing-plan-desc">{' ' + t('landing.perfectForTryingOutReviio')}<div className="lp-pricing-plan-desc-note">{t('landing.noCreditCardRequiredNoHidden')}</div>
              </div>
                <div className="lp-pricing-amount"><span className="currency">$</span>0</div>
                <div className="lp-pricing-period">{t('landing.foreverFree')}</div>
                <div className="lp-pricing-features">
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.1Location')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.10AiRepliesMonth')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('common.allReplyModes')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.fixedStarBasedReplies')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('common.basicAnalytics')}</div>
                  <div className="lp-pricing-feature disabled"><XIcon />{' ' + t('landing.replyPacks')}</div>
                </div>
                <Link to="/signup?plan=FREE" className="lp-pricing-cta outline" id="pricing-free-cta" onClick={() => trackPricingClick('free')}>{t('landing.getStarted')}</Link>
              </div>
              {/* Basic */}
              <div className="lp-pricing-card popular lp-reveal lp-reveal-delay-2">
                <div className="lp-pricing-popular-badge">{t('landing.mostPopular')}</div>
                <div className="lp-pricing-plan-name">{t('landing.basic')}</div>
                <div className="lp-pricing-plan-desc">{t('landing.forGrowingBusinesses')}</div>
                <div className="lp-pricing-amount lp-pricing-amount-strikethrough">
                  <span className="currency">$</span>{isAnnual ? '12.42' : '19'}
                </div>
                <div className="lp-pricing-period">{t('landing.perMonth')}{isAnnual && <span className="lp-pricing-period-note">{t('landing.billedYearlyAt149')}</span>}</div>
                <div className="lp-pricing-features">
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.1Location')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + (isAnnual ? t('landing.2400AiRepliesYear') : t('landing.200AiRepliesMonth'))}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('common.allReplyModes')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.fixedStarBasedReplies')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('common.contextUploadsPdfImages')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('common.buyExtraReplyPacks')}</div>
                </div>
                <div className="lp-promo-text">{t('landing.startFreeYour1MonthPro')}</div>
                <Link to={`/signup?plan=${isAnnual ? 'BASIC_ANNUAL' : 'BASIC'}`} className="lp-pricing-cta gradient" id="pricing-basic-cta" onClick={() => { window.scrollTo(0, 0); trackPricingClick(isAnnual ? 'basic_annual' : 'basic'); }}>{t('landing.startFreeUpgrade')}</Link>
              </div>
              {/* Pro */}
              <div className="lp-pricing-card lp-reveal lp-reveal-delay-3">
                <div className="lp-pricing-plan-name">{t('landing.pro')}</div>
                <div className="lp-pricing-plan-desc">{t('landing.forMultiLocationBusinesses')}</div>
                <div className="lp-pricing-amount lp-pricing-amount-strikethrough">
                  <span className="currency">$</span>{isAnnual ? '25.75' : '39'}
                </div>
                <div className="lp-pricing-period">{t('landing.perMonth')}{isAnnual && <span className="lp-pricing-period-note">{t('landing.billedYearlyAt309')}</span>}</div>
                <div className="lp-pricing-features">
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.upTo5Locations')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + (isAnnual ? t('landing.12000AiRepliesYear') : t('landing.1000AiRepliesMonthPooled'))}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('common.allReplyModes')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.teamRolesInviteResponders')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('landing.fullAnalyticsAuditLog')}</div>
                  <div className="lp-pricing-feature"><CheckIcon />{' ' + t('common.buyExtraReplyPacks')}</div>
                </div>
                <div className="lp-promo-text">{t('landing.startFreeYour1MonthPro')}</div>
                <Link to={`/signup?plan=${isAnnual ? 'PRO_ANNUAL' : 'PRO'}`} className="lp-pricing-cta outline" id="pricing-pro-cta" onClick={() => { window.scrollTo(0, 0); trackPricingClick(isAnnual ? 'pro_annual' : 'pro'); }}>{t('landing.startFreeUpgrade')}</Link>
              </div>
            </div>
            {/* Add-on packs */}
            <div className="lp-pricing-addons lp-reveal">
              <div className="lp-addon-item">
                <h3>{t('landing.100Replies5')}</h3>
                <p>{t('landing.005PerReplyExpiresMonthly')}</p>
              </div>
              <div className="lp-addon-divider" />
              <div className="lp-addon-item">
                <h3>{t('landing.500Replies15')}</h3>
                <p>{t('landing.003PerReplyExpiresMonthly')}</p>
              </div>
              <div className="lp-addon-divider" />
              <div className="lp-addon-item">
                <h3>{t('landing.5000Replies39')}</h3>
                <p>{t('landing.00078PerReplyExpiresMonthly')}</p>
              </div>
            </div>
          </div>
        </section>



        {/* ===== BLOG PREVIEW ===== */}
        {blogPosts.length > 0 && (
          <section className="lp-blog-preview" id="blog">
            <script type="application/json" id="initial-blog-posts" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPosts).replace(/</g, '\\u003c') }} />
            <div className="lp-container">
              <div className="lp-section-label lp-section-label-light">{t('landing.fromOurBlog')}</div>
              <h2 className="lp-section-title lp-section-title-light">{t('landing.latestInsights')}</h2>
              <p className="lp-section-subtitle lp-section-subtitle-light">{t('landing.tipsStrategiesAndGuidesOnReview')}</p>
              <div className="lp-blog-grid">
                {blogPosts.map((post, index) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className={`lp-blog-card ${index === 1 ? 'lp-blog-card-offset' : ''}`}
                  >
                    {post.coverImageUrl && (
                      <img src={post.coverImageUrl} alt={post.title} className="lp-blog-card-cover" width="400" height="250" loading="lazy" />
                    )}
                    <div className="lp-blog-card-body">
                      {post.tags && post.tags.length > 0 && (
                        <div className="lp-blog-card-tags">
                          {post.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="lp-blog-card-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                      <h3 className="lp-blog-card-title">{post.title}</h3>
                      {post.excerpt && <p className="lp-blog-card-excerpt">{post.excerpt}</p>}
                      <div className="lp-blog-card-footer">
                        <span>{new Date(post.publishedAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="lp-blog-card-read">{t('landing.read')}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="lp-blog-view-all">
                <Link to="/blog" className="lp-btn-primary lp-btn-primary-md">{t('landing.viewAllArticles')}</Link>
              </div>
            </div>
          </section>
        )}

        {/* ===== CONTACT ===== */}
        <section className="lp-contact" id="contact">
          <div className="lp-container">
            <div className="lp-section-label lp-reveal">{t('common.contactUs')}</div>
            <h2 className="lp-section-title lp-reveal">{t('landing.getInTouch')}</h2>
            <p className="lp-section-subtitle lp-reveal">{t('landing.haveQuestionsWeDLoveTo')}</p>
            <div className="lp-contact-card lp-reveal">
              <form className="lp-contact-form" onSubmit={handleSubmit}>
                <div className="lp-form-row">
                  <div className="lp-form-group">
                    <label htmlFor="contact-name">{t('common.fullName')}</label>
                    <input type="text" id="contact-name" name="name" placeholder={t('common.johnDoe')} required />
                  </div>
                  <div className="lp-form-group">
                    <label htmlFor="contact-email">{t('common.emailAddress')}</label>
                    <input type="email" id="contact-email" name="email" placeholder={t('landing.johnBusinessCom')} required />
                  </div>
                </div>
                <div className="lp-form-group">
                  <label htmlFor="contact-subject">{t('common.subject')}</label>
                  <select id="contact-subject" name="subject">
                    <option value="general">{t('landing.generalInquiry')}</option>
                    <option value="sales">{t('landing.salesPricing')}</option>
                    <option value="support">{t('landing.technicalSupport')}</option>
                    <option value="partnership">{t('landing.partnership')}</option>
                  </select>
                </div>
                <div className="lp-form-group">
                  <label htmlFor="contact-message">{t('common.message')}</label>
                  <textarea id="contact-message" name="message" placeholder={t('landing.tellUsHowWeCanHelp')} required />
                </div>
                <button
                  type="submit"
                  className={`lp-btn-primary lp-btn-primary-lg lp-contact-submit ${formSent ? 'lp-contact-submit-success' : ''}`}
                  id="contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('landing.sending') : formSent ? t('landing.messageSent') : t('landing.sendMessage')}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* ===== FOOTER ===== */}
      <SiteFooter />
    </>
  );
}
